import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Navbar } from './components/navbar/navbar';
import { AuthService } from 'auth';
import { StaffMealsService } from './core/services/staff-meals.service';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { filter, map, catchError } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { RateMealModalComponent } from './shared/components/modals/rate-meal-modal/rate-meal-modal';
import { RateVendorModalComponent } from './shared/components/modals/rate-vendor-modal/rate-vendor-modal';

export interface Meal {
  id: string;
  name: string;
  vendor: string;
  price: number;
  portionsLeft: number;
  category: 'Breakfast' | 'Lunch' | 'Dinner' | string;
  dietary: string[];
  image: string;
  cafeteria: string;
  dateId: number;
  description?: string;
  calories?: number;
  protein?: number;
  carbohydrates?: number;
  fat?: number;
}

export interface CartItem {
  meal: Meal;
  quantity: number;
}

export interface DateOption {
  id: number;
  name: string;
  num: number;
  label: string;
  isoDate?: string;
}

export interface Booking {
  id: string;
  mealName: string;
  vendor: string;
  vendorId?: string;
  price: number;
  date: string;
  rawDate?: string;
  cafeteria: string;
  status:  string;
  portion: string;
  claimCode?: string;
  originalPrice?: number;
  subsidyApplied?: number;
  amountPaid?: number;
  image?: string;
  category?: string;
  hasMealRating?: boolean;
  hasVendorRating?: boolean;
  servingTimeStart?: string;
  servingTimeEnd?: string;
}

export interface Transaction {
  id: string;
  date: string;
  description: string;
  amount: number;
  type: 'Debit' | 'Credit' | 'Subsidy Co-Pay';
  status: 'Success' | 'Failed';
}

export interface SubsidyInfo {
  isEligible: boolean;
  ruleApplied?: string;
  mode: string;
  fixedAmount: number;
  percentageDiscount: number;
  dailyCap: number;
  remainingToday: number;
}

export interface BookedItem {
  mealName: string;
  vendor: string;
  image?: string;
  cafeteria: string;
  category?: string;
  originalPrice: number;
  subsidyApplied: number;
  amountPaid: number;
  claimCode: string;
  bookingReference: string;
}

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    Navbar,
    RouterOutlet,
    RateMealModalComponent,
    RateVendorModalComponent,
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App implements OnInit {
  title = 'staff-app';

  // Navigation State
  currentView: 'home' | 'bookings' | 'transactions' | 'checkout' | 'validate-qr' | 'profile' = 'home';
  bookingState: 'idle' | 'loading' | 'success' | 'error' = 'idle';

  // Selected Meal for Checkout Flow
  selectedMealForCheckout: Meal | null = null;
  lastBookingReference: string = '';
  lastClaimCode: string = '';
  lastOriginalPrice: number | null = null;
  lastSubsidyApplied: number | null = null;
  lastAmountPaid: number | null = null;
  // All items successfully booked in the most recent checkout (supports multiple cart items)
  lastBookedItems: BookedItem[] = [];

  subsidyInfo: SubsidyInfo | null = null;
  subsidyRate: number = 45;
  profileName: string = '';
  bookingWindow: any = null;

  // Search & Filters State
  searchQuery: string = '';
  selectedCafeteria: string = '';
  selectedCategory: string = 'All Meals';
  selectedDateId: number = 1;
  selectedDietaryTags: string[] = [];

  cafeterias: any[] = [];
  dates: DateOption[] = [];
  mealCategoryConfigs: any[] = [];
  dietaryTags: any[] = [];
  isDietaryTagsLoaded: boolean = false;

  // Limit States
  mealsUsedToday: number = 0;
  dailyMealLimit: number = 2;

  // Modal States
  isFilterModalOpen: boolean = false;
  isCartOpen: boolean = false;
  isLoadingMenu: boolean = false;

  // Rating States
  pendingRatingsQueue: any[] = [];
  currentRatingBooking: Booking | null = null;
  showMealRatingModal: boolean = false;
  showVendorRatingModal: boolean = false;

  // Filter Modal selections
  filterCategory: string = 'All Meals';
  filterDietary: { [key: string]: boolean } = {

  };

  // Toast System
  toasts: { id: number; message: string; type: 'success' | 'error' | 'info' }[] = [];
  private toastIdCounter = 0;

  // Cart State
  cart: CartItem[] = [];

  // API Lists
  meals: Meal[] = [];
  bookings: Booking[] = [];
  transactions: Transaction[] = [];
  private lastRequestKey: string = '';

  constructor(
    public authService: AuthService,
    private staffMealsService: StaffMealsService,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) {
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      const url = event.urlAfterRedirects || event.url;
      const segment = url.split('/')[1];
      if (['home', 'bookings', 'transactions', 'checkout', 'validate-qr', 'profile'].includes(segment)) {
        this.currentView = segment as any;
        if (!this.authService.isLoggedIn()) {
          this.router.navigate(['/login']);
        }
      }
    });
  }

  ngOnInit() {
    this.dates = this.generateDates();
    if (this.dates.length > 0) {
      this.selectedDateId = this.dates[0].id;
    }

    const savedCart = localStorage.getItem('bucca_cart');
    if (savedCart) {
      try {
        this.cart = JSON.parse(savedCart);
      } catch (e) {
        console.error('Failed to parse saved cart', e);
        this.cart = [];
      }
    }

    if (this.authService.isLoggedIn()) {
      if (this.authService.role() !== 'Staff') {
        this.handleLogout();
        return;
      }
      this.loadConfigurationsAndData();
      const currentUrl = this.router.url;
      if (currentUrl === '/' || currentUrl === '/login' || currentUrl.startsWith('/login')) {
        this.router.navigate(['/home']);
      }
    } else {
      const currentPath = window.location.pathname.toLowerCase();
      const publicPaths = ['/login', '/auth/', '/account/activate', '/account/reset-password', '/set-password'];
      const isPublicRoute = publicPaths.some((p) => currentPath.includes(p));
      if (!isPublicRoute) {
        this.router.navigate(['/login']);
      }
    }
  }

  handleLoginSuccess(event: any) {
    this.router.navigate(['/home']).then(() => {
      this.loadConfigurationsAndData();
    });
  }

  onRouteActivated(component: any) {
    if (component.loginSuccess) {
      component.loginSuccess.subscribe((event: any) => {
        this.handleLoginSuccess(event);
      });
    }
  }

  applyProfileData(profileData: any) {
    this.profileName = profileData.fullName || '';
    if (profileData.organizationName) {
      localStorage.setItem('organisationName', profileData.organizationName);
    }

    if (profileData.mealAccessLimit !== undefined) {
      this.dailyMealLimit = profileData.mealAccessLimit;
    } else if (profileData.maxMealsPerDay !== undefined) {
      this.dailyMealLimit = profileData.maxMealsPerDay;
    } else if (profileData.dailyMealLimit !== undefined) {
      this.dailyMealLimit = profileData.dailyMealLimit;
    } else if (profileData.mealLimit !== undefined) {
      this.dailyMealLimit = profileData.mealLimit;
    } else if (profileData.mealAccess && profileData.mealAccess.maxMealsPerDay !== undefined) {
      this.dailyMealLimit = profileData.mealAccess.maxMealsPerDay;
    } else if (profileData.mealAccessRule && profileData.mealAccessRule.maxMealsPerDay !== undefined) {
      this.dailyMealLimit = profileData.mealAccessRule.maxMealsPerDay;
    }

    if (profileData.bookingWindow) {
      this.bookingWindow = profileData.bookingWindow;
    }

    if (profileData.mealsBookedForNextDate !== undefined) {
      this.mealsUsedToday = profileData.mealsBookedForNextDate;
    }

    const subsidy = profileData.subsidy || {};

    if (subsidy.maxMealsPerDay !== undefined) {
      this.dailyMealLimit = subsidy.maxMealsPerDay;
    } else if (subsidy.dailyMealLimit !== undefined) {
      this.dailyMealLimit = subsidy.dailyMealLimit;
    }
    const isEligible = subsidy.isEligible !== undefined ? subsidy.isEligible : (profileData.isSubsidyEligible !== undefined ? profileData.isSubsidyEligible : true);

    this.subsidyInfo = {
      isEligible: isEligible,
      ruleApplied: subsidy.ruleApplied || 'Global',
      mode: subsidy.mode || (subsidy.fixedAmount ? 'FixedAmount' : 'Percentage'),
      fixedAmount: subsidy.fixedAmount || 0,
      percentageDiscount: subsidy.percentageDiscount || 0,
      dailyCap: subsidy.dailyCap || 0,
      remainingToday: subsidy.remainingToday !== undefined ? subsidy.remainingToday : 0
    };

    const modeStr = String(subsidy.mode || '').toLowerCase();
    if (!isEligible) {
      this.subsidyRate = 0;
    } else if (modeStr === 'percentage' || modeStr === 'percentagediscount' || subsidy.mode === 2) {
      this.subsidyRate = subsidy.percentageDiscount || 0;
    } else {
      this.subsidyRate = (subsidy.fixedAmount || subsidy.remainingToday) ? 0 : (isEligible ? 45 : 0);
    }
  }

  // Re-fetches just the profile (mealAccessLimit, mealsBookedForNextDate, subsidy)
  // so the daily meal limit reflects the server's count right after a booking or
  // cancellation, without waiting for a full page reload.
  refreshProfile() {
    this.staffMealsService.getProfile().subscribe({
      next: (res: any) => {
        if (res && res.data) {
          this.applyProfileData(res.data);
          this.cdr.detectChanges();
        }
      },
      error: (err) => {
        console.error('Error refreshing profile', err);
      }
    });
  }

  loadConfigurationsAndData() {
    const orgId = this.authService.getOrganizationId();
    const userId = this.authService.getStaffId() || this.authService.getUserId();
    if (!orgId || !userId) return;

    this.cdr.detectChanges();

    // Fetch essential initial data in parallel
    forkJoin({
      profile: this.staffMealsService.getProfile(),
      cafeterias: this.staffMealsService.getCafeterias(orgId),
      bookings: this.staffMealsService.getBookingHistory({
        StaffUserId: userId,
        OrganizationId: orgId,
        PageNumber: 1,
        PageSize: 100
      })
    }).subscribe({
      next: (res: any) => {
        // 1. Process Profile
        if (res.profile && res.profile.data) {
          this.applyProfileData(res.profile.data);
        }

        // 2. Process Bookings
        let bookingsList: any[] = [];
        if (res.bookings) {
          if (res.bookings.data && Array.isArray(res.bookings.data.items)) {
            bookingsList = res.bookings.data.items;
          } else if (Array.isArray(res.bookings.items)) {
            bookingsList = res.bookings.items;
          } else if (Array.isArray(res.bookings.data)) {
            bookingsList = res.bookings.data;
          } else if (Array.isArray(res.bookings)) {
            bookingsList = res.bookings;
          }
        }

        this.bookings = bookingsList.map((item: any) => {
          let formattedDate = '';
          const dateVal = item.availableDate || item.bookingDate || item.orderDate;
          if (dateVal) {
            try {
              const d = new Date(dateVal);
              formattedDate = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
            } catch (e) {
              formattedDate = dateVal;
            }
          }

          return {
            id: item.bookingId || item.id,
            mealName: item.mealName,
            vendor: item.vendorName || '-',
            price: item.amountPaid || item.mealPrice || 0,
            date: formattedDate,
            cafeteria: item.cafeteriaName || '-',
            status: item.status || '-',
            portion: `${item.portionQuantity || 1} Portion`,
            claimCode: item.claimPasscode || item.claimCode,
            originalPrice: item.originalPrice ?? item.mealPrice ?? item.amountPaid ?? 0,
            subsidyApplied: item.subsidyApplied ?? 0,
            amountPaid: item.amountPaid ?? item.mealPrice ?? 0,
            image: item.mealImageUrl || item.imageUrl,
            category: item.mealCategoryName || item.categoryName,
            hasMealRating: !!(item.hasMealRating || item.mealFeedbackSubmittedAt),
            hasVendorRating: !!(item.hasVendorRating || item.vendorFeedbackSubmittedAt),
            vendorId: item.vendorId,
            servingTimeStart: item.servingTimeStart || item.servingTime || '-',
            servingTimeEnd: item.servingTimeEnd || '-',
            rawDate: item.availableDate || item.bookingDate || item.orderDate
          };
        });
        this.updateMealsUsedToday(this.bookings);
        this.updateTransactionsFromBookings(this.bookings);

        // 3. Process Cafeterias
        let cafeteriasList: any[] = [];
        if (res.cafeterias) {
          if (Array.isArray(res.cafeterias.items)) {
            cafeteriasList = res.cafeterias.items;
          } else if (res.cafeterias.data && Array.isArray(res.cafeterias.data.items)) {
            cafeteriasList = res.cafeterias.data.items;
          } else if (Array.isArray(res.cafeterias.data)) {
            cafeteriasList = res.cafeterias.data;
          } else if (Array.isArray(res.cafeterias)) {
            cafeteriasList = res.cafeterias;
          }
        }

        if (cafeteriasList.length > 0) {
          this.cafeterias = cafeteriasList;
          this.selectedCafeteria = this.cafeterias[0].id;
          this.loadMenu();
        } else {
          this.showToast('Failed to load cafeterias', 'error');
        }

        this.cdr.detectChanges();
        this.checkPendingRatings();
      },
      error: (err: any) => {
        console.error('Error loading startup configuration and data', err);
        this.showToast(this.extractErrorMessage(err, 'Error loading startup configuration from server'), 'error');
        this.cdr.detectChanges();
      }
    });
  }

  checkPendingRatings() {
    this.staffMealsService.getPendingRatings().subscribe({
      next: (res: any) => {
        const list = res?.data || res || [];
        if (list && list.length > 0) {
          this.pendingRatingsQueue = [...list];
          this.showNextPendingRating();
        }
      },
      error: (err: any) => {
        console.error('Error fetching pending ratings', err);
      }
    });
  }

  showNextPendingRating() {
    if (this.pendingRatingsQueue.length === 0) {
      this.currentRatingBooking = null;
      this.showMealRatingModal = false;
      this.showVendorRatingModal = false;
      this.cdr.detectChanges();
      return;
    }

    const currentItem = this.pendingRatingsQueue[0];
    
    // Construct a Booking object compatible with the rate modals
    this.currentRatingBooking = {
      id: currentItem.bookingId,
      mealName: currentItem.mealName,
      vendor: currentItem.vendorName || 'Unknown Vendor',
      cafeteria: currentItem.cafeteriaName || 'Main Cafeteria',
      category: currentItem.mealCategory || 'Lunch',
      date: this.formatBookingDate(currentItem.mealAvailableDate),
      price: 0,
      portion: '1 Portion',
      status: 'Completed',
      hasMealRating: !currentItem.needsMealRating,
      hasVendorRating: !currentItem.needsVendorRating,
      image: currentItem.mealImageUrl || ''
    };

    if (currentItem.needsMealRating) {
      this.showMealRatingModal = true;
      this.showVendorRatingModal = false;
    } else if (currentItem.needsVendorRating) {
      this.showMealRatingModal = false;
      this.showVendorRatingModal = true;
    } else {
      // Nothing needs to be rated on this item, remove and move to next
      this.pendingRatingsQueue.shift();
      this.showNextPendingRating();
    }
    this.cdr.detectChanges();
  }

  handleMealRated(event: { bookingId: string; rating: number; comment?: string }) {
    if (this.pendingRatingsQueue.length > 0 && this.pendingRatingsQueue[0].bookingId === event.bookingId) {
      const currentItem = this.pendingRatingsQueue[0];
      currentItem.needsMealRating = false;
      
      this.showToast('Meal rated successfully', 'success');

      if (currentItem.needsVendorRating) {
        // Same booking needs vendor rating, transition immediately
        this.showMealRatingModal = false;
        this.showVendorRatingModal = true;
        this.cdr.detectChanges();
      } else {
        // Done with this item, move to next
        this.pendingRatingsQueue.shift();
        this.showNextPendingRating();
      }
    } else {
      this.pendingRatingsQueue.shift();
      this.showNextPendingRating();
    }
  }

  handleMealRatingClosed() {
    if (this.pendingRatingsQueue.length > 0) {
      const currentItem = this.pendingRatingsQueue[0];
      currentItem.needsMealRating = false; // treat as handled/skipped

      if (currentItem.needsVendorRating) {
        this.showMealRatingModal = false;
        this.showVendorRatingModal = true;
        this.cdr.detectChanges();
      } else {
        this.pendingRatingsQueue.shift();
        this.showNextPendingRating();
      }
    } else {
      this.showMealRatingModal = false;
      this.showVendorRatingModal = false;
      this.cdr.detectChanges();
    }
  }

  handleVendorRated(event: { bookingId: string; rating: number; note?: string }) {
    if (this.pendingRatingsQueue.length > 0 && this.pendingRatingsQueue[0].bookingId === event.bookingId) {
      this.pendingRatingsQueue[0].needsVendorRating = false;
      this.showToast('Vendor rated successfully', 'success');
    }
    this.pendingRatingsQueue.shift();
    this.showNextPendingRating();
  }

  handleVendorRatingClosed() {
    this.pendingRatingsQueue.shift();
    this.showNextPendingRating();
  }

  formatBookingDate(dateStr: string): string {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const dayName = days[d.getDay()];
      const dayNum = d.getDate();
      
      let suffix = 'th';
      if (dayNum === 1 || dayNum === 21 || dayNum === 31) suffix = 'st';
      else if (dayNum === 2 || dayNum === 22) suffix = 'nd';
      else if (dayNum === 3 || dayNum === 23) suffix = 'rd';

      return `${dayName} ${dayNum}${suffix}`;
    } catch (e) {
      return dateStr;
    }
  }

  handleFiltersApplied(event: { category: string; dietary: string[]; }) {
    this.selectedCategory = event.category;
    this.selectedDietaryTags = event.dietary;
    this.loadMenu();
  }

  isLogoutModalVisible = false;

  handleLogout() {
    this.isLogoutModalVisible = true;
  }

  confirmLogout() {
    this.isLogoutModalVisible = false;
    this.authService.logout();
    this.showToast('Logged out successfully', 'info');
    this.meals = [];
    this.bookings = [];
    this.transactions = [];
    this.cart = [];
  }

  get userName(): string {
    const userInfo: any = this.authService.accessTokenInfo();
    return this.profileName || userInfo?.fullName || userInfo?.name || userInfo?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'] || 'User';
  }

  get organisationName(): string {
    const userInfo: any = this.authService.accessTokenInfo();
    return localStorage.getItem('organisationName') || userInfo?.organisationName || userInfo?.organizationName || 'Staff';
  }

  get todayLabel(): string {
    const today = new Date();
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dayName = days[today.getDay()];
    const dayNum = today.getDate();

    const pr = new Intl.PluralRules('en-US', { type: 'ordinal' });
    const suffix = pr.select(dayNum) === 'one' ? 'st' : pr.select(dayNum) === 'two' ? 'nd' : pr.select(dayNum) === 'few' ? 'rd' : 'th';

    const monthFormatter = new Intl.DateTimeFormat('en-US', { month: 'long' });
    const monthName = monthFormatter.format(today);

    return `Today ${dayName}, ${dayNum}${suffix} ${monthName}`;
  }

  generateDates(): DateOption[] {
    const days = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
    const result: DateOption[] = [];
    const today = new Date();

    // Start from tomorrow (i = 1) — the API rejects bookings for today or
    // past dates ("Meals cannot be booked for today or past dates"), so
    // today is never a valid option and would just confuse staff.
    for (let i = 1; i <= 7; i++) {
      const date = new Date();
      date.setDate(today.getDate() + i);

      const dayName = days[date.getDay()];
      const dayNum = date.getDate();

      const formatter = new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
      let label = formatter.format(date);

      const pr = new Intl.PluralRules('en-US', { type: 'ordinal' });
      const suffix = pr.select(dayNum) === 'one' ? 'st' : pr.select(dayNum) === 'two' ? 'nd' : pr.select(dayNum) === 'few' ? 'rd' : 'th';
      label = label.replace(/\d+$/, dayNum + suffix);

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const isoDate = `${year}-${month}-${day}`;

      result.push({
        id: i,
        name: dayName,
        num: dayNum,
        label: label,
        isoDate: isoDate
      });
    }
    return result;
  }

  loadCafeterias() {
    const orgId = this.authService.getOrganizationId();
    if (!orgId) {
      this.showToast('No organization ID found. Please log in.', 'error');
      return;
    }
    this.staffMealsService.getCafeterias(orgId).subscribe({
      next: (res) => {
        let list: any[] = [];
        if (res) {
          if (Array.isArray(res.items)) {
            list = res.items;
          } else if (res.data && Array.isArray(res.data.items)) {
            list = res.data.items;
          } else if (Array.isArray(res.data)) {
            list = res.data;
          } else if (Array.isArray(res)) {
            list = res;
          }
        }

        if (list.length > 0) {
          this.cafeterias = list;
          this.selectedCafeteria = this.cafeterias[0].id;
          this.loadMenu();
          this.cdr.detectChanges();
        } else {
          this.showToast('Failed to load cafeterias', 'error');
        }
      },
      error: (err) => {
        console.error('Error loading cafeterias', err);
        this.showToast('Error loading cafeterias from server', 'error');
        this.cdr.detectChanges();
      }
    });
  }

  ensureMealCategoryConfigs(callback: () => void) {
    if (this.mealCategoryConfigs.length > 0 || !this.selectedCategory || this.selectedCategory === 'All Meals') {
      callback();
      return;
    }

    const orgId = this.authService.getOrganizationId();
    if (!orgId) {
      callback();
      return;
    }

    this.staffMealsService.getMealCategoryConfigs(orgId).subscribe({
      next: (resConfig) => {
        let configList: any[] = [];
        if (resConfig) {
          if (Array.isArray(resConfig)) {
            configList = resConfig;
          } else if (Array.isArray(resConfig.data)) {
            configList = resConfig.data;
          } else if (resConfig.data && Array.isArray(resConfig.data.items)) {
            configList = resConfig.data.items;
          } else if (Array.isArray(resConfig.items)) {
            configList = resConfig.items;
          }
        }
        this.mealCategoryConfigs = configList;
        callback();
      },
      error: (err) => {
        console.error('Error loading meal category configs on demand', err);
        callback();
      }
    });
  }

  ensureDietaryTags(callback: () => void) {
    if (this.isDietaryTagsLoaded) {
      callback();
      return;
    }

    const orgId = this.authService.getOrganizationId();
    if (!orgId) {
      callback();
      return;
    }

    this.staffMealsService.getDietaryTags(orgId).subscribe({
      next: (resTags) => {
        this.isDietaryTagsLoaded = true;
        let tagsList: any[] = [];
        if (resTags) {
          if (Array.isArray(resTags)) {
            tagsList = resTags;
          } else if (Array.isArray(resTags.data)) {
            tagsList = resTags.data;
          } else if (resTags.data && Array.isArray(resTags.data.items)) {
            tagsList = resTags.data.items;
          } else if (Array.isArray(resTags.items)) {
            tagsList = resTags.items;
          }
        }
        this.dietaryTags = tagsList.filter(t => t && t.isAvailableForStaffs === true);
        callback();
      },
      error: (err) => {
        console.error('Error loading dietary tags on demand', err);
        this.isDietaryTagsLoaded = true;
        callback();
      }
    });
  }

  loadMenu() {
    this.ensureMealCategoryConfigs(() => {
      this.ensureDietaryTags(() => {
        this.executeLoadMenu();
      });
    });
  }

  executeLoadMenu() {
    const orgId = this.authService.getOrganizationId();
    const userId = this.authService.getStaffId() || this.authService.getUserId();
    if (!orgId || !this.selectedCafeteria || !userId) {
      console.log('DEBUG: loadMenu exit early - missing params. orgId:', orgId, 'selectedCafeteria:', this.selectedCafeteria, 'userId:', userId);
      return;
    }

    const selectedDateOption = this.dates.find(d => d.id === this.selectedDateId);
    const dateStr = selectedDateOption?.isoDate ? `${selectedDateOption.isoDate}T12:00:00.000Z` : undefined;

    if (!dateStr) {
      console.log('DEBUG: loadMenu exit early - no dateStr');
      return;
    }

    let mealCategoryConfigId: string | undefined = undefined;
    if (this.selectedCategory && this.selectedCategory !== 'All Meals') {
      const config = this.mealCategoryConfigs.find(
        (c: any) => c.name.toLowerCase() === this.selectedCategory.toLowerCase()
      );
      mealCategoryConfigId = config?.id;
    }

    const dietaryTagIds: string[] = [];
    if (this.selectedDietaryTags && this.selectedDietaryTags.length > 0) {
      this.selectedDietaryTags.forEach(name => {
        const tag = this.dietaryTags.find((t: any) => t.name.toLowerCase() === name.toLowerCase());
        if (tag && tag.id) {
          dietaryTagIds.push(tag.id);
        }
      });
    }

    const requestKey = `${dateStr}_${this.selectedCafeteria}_${mealCategoryConfigId || 'all'}_${dietaryTagIds.join(',')}`;
    console.log('DEBUG: loadMenu starting. requestKey:', requestKey, 'selectedDateId:', this.selectedDateId);
    this.lastRequestKey = requestKey;
    this.isLoadingMenu = true;

    this.staffMealsService.getDailyMenu({
      StaffUserId: userId,
      CafeteriaId: this.selectedCafeteria,
      TargetDate: dateStr,
      MealCategoryConfigId: mealCategoryConfigId,
      DietaryTagIds: dietaryTagIds.length > 0 ? dietaryTagIds : undefined
    }).subscribe({
      next: (res) => {
        if (this.lastRequestKey !== requestKey) {
          console.log('DEBUG: ignoring stale menu response. lastRequestKey:', this.lastRequestKey, 'requestKey:', requestKey);
          return;
        }
        this.isLoadingMenu = false;
        console.log('DEBUG: loadMenu raw res:', res);
        let list: any[] = [];
        if (res && res.data && Array.isArray(res.data.meals)) {
          list = res.data.meals;
        } else if (res && Array.isArray(res.data)) {
          list = res.data;
        } else if (res && Array.isArray(res.meals)) {
          list = res.meals;
        }
        console.log('DEBUG: loadMenu parsed list:', list);

        if (list.length > 0) {
          this.meals = list.map((item: any) => ({
            id: item.id,
            name: item.name,
            vendor: item.vendorName || 'Balance Bowl',
            price: item.price,
            portionsLeft: item.isSoldOut ? 0 : (item.availableQuantity ?? item.inventoryCount ?? 0),
            category: item.mealCategoryName || 'Lunch',
            dietary: item.dietaryTagNames || (item.dietaryTags || []).map((t: any) => t.name) || [],
            image: item.imageUrl || 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=500&auto=format&fit=crop&q=80',
            cafeteria: this.cafeterias.find(c => c.id === this.selectedCafeteria)?.name || this.selectedCafeteria,
            dateId: this.selectedDateId,
            description: item.description,
            calories: item.calories,
            protein: item.protein,
            carbohydrates: item.carbohydrates,
            fat: item.fat
          }));
          this.syncCartWithLoadedMeals();
          console.log('DEBUG: loadMenu mapped this.meals:', this.meals);
        } else {
          this.meals = [];
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        if (this.lastRequestKey !== requestKey) {
          return;
        }
        this.isLoadingMenu = false;
        console.error('Error loading daily menu', err);
        this.showToast(this.extractErrorMessage(err, 'Error loading daily menu'), 'error');
        this.meals = [];
        this.cdr.detectChanges();
      }
    });
  }

  loadBookingHistory() {
    const orgId = this.authService.getOrganizationId();
    const userId = this.authService.getStaffId();
    if (!orgId || !userId) {
      return;
    }

    this.staffMealsService.getBookingHistory({
      StaffUserId: userId,
      OrganizationId: orgId,
      PageNumber: 1,
      PageSize: 100
    }).subscribe({
      next: (res) => {
        let list: any[] = [];
        if (res) {
          if (res.data && Array.isArray(res.data.items)) {
            list = res.data.items;
          } else if (Array.isArray(res.items)) {
            list = res.items;
          } else if (Array.isArray(res.data)) {
            list = res.data;
          } else if (Array.isArray(res)) {
            list = res;
          }
        }

        if (list.length > 0 || (res && (res.status || res.items || res.data))) {
          this.bookings = list.map((item: any) => {
            let formattedDate = '';
            const dateVal = item.availableDate || item.bookingDate || item.orderDate;
            if (dateVal) {
              try {
                const d = new Date(dateVal);
                formattedDate = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
              } catch (e) {
                formattedDate = dateVal;
              }
            }

            return {
              id: item.bookingId || item.id,
              mealName: item.mealName,
              vendor: item.vendorName || 'Balance Bowl',
              price: item.amountPaid || item.mealPrice || 0,
              date: formattedDate,
              cafeteria: item.cafeteriaName || 'Main Cafeteria',
              status: item.status === 'Confirmed' ? 'Pending' : item.status,
              portion: `${item.portionQuantity || 1} Portion`,
              claimCode: item.claimPasscode || item.claimCode,
              originalPrice: item.originalPrice ?? item.mealPrice ?? item.amountPaid ?? 0,
              subsidyApplied: item.subsidyApplied ?? 0,
              amountPaid: item.amountPaid ?? item.mealPrice ?? 0,
              image: item.mealImageUrl || item.imageUrl,
              category: item.mealCategoryName || item.categoryName,
              hasMealRating: !!(item.hasMealRating || item.mealFeedbackSubmittedAt),
              hasVendorRating: !!(item.hasVendorRating || item.vendorFeedbackSubmittedAt),
              vendorId: item.vendorId,
              servingTimeStart: item.servingTimeStart || item.servingTime || '12:30 PM',
              servingTimeEnd: item.servingTimeEnd || '14:00 PM',
              rawDate: item.availableDate || item.bookingDate || item.orderDate
            };
          });

          // Compute limit usage and populate transactions
          this.updateMealsUsedToday(this.bookings);
          this.updateTransactionsFromBookings(this.bookings);
        } else {
          this.bookings = [];
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading booking history', err);
        this.showToast(this.extractErrorMessage(err, 'Error loading booking history from server'), 'error');
        this.cdr.detectChanges();
      }
    });
  }

  updateMealsUsedToday(bookings: Booking[]) {
    // Rely on the value returned from the profile endpoint (mealsBookedForNextDate).
    // If it's 0 and there are bookings, we might still want to avoid manual counting here,
    // as it conflicts with the server's tracking of 'next booking date'.
  }

  calculateSubsidy(price: number): number {
    if (!price || price <= 0) return 0;
    if (!this.subsidyInfo) {
      if (this.subsidyRate > 0) {
        return Math.round(price * (this.subsidyRate / 100));
      }
      return 0;
    }

    const sub = this.subsidyInfo;
    if (sub.isEligible === false) return 0;

    const modeStr = String(sub.mode || '').toLowerCase();
    const isFixedMode = modeStr === 'fixedamount' || modeStr === 'fixed' || modeStr === 'fixedrate' || modeStr === '1' || (sub.fixedAmount > 0 && !sub.percentageDiscount);

    if (isFixedMode) {
      let cap = 0;
      if (sub.remainingToday !== undefined && sub.remainingToday !== null && sub.remainingToday > 0) {
        cap = sub.fixedAmount > 0 ? Math.min(sub.fixedAmount, sub.remainingToday) : sub.remainingToday;
      } else if (sub.dailyCap > 0) {
        cap = sub.fixedAmount > 0 ? Math.min(sub.fixedAmount, sub.dailyCap) : sub.dailyCap;
      } else {
        cap = sub.fixedAmount || 0;
      }
      return Math.min(price, Math.round(cap));
    } else {
      const rate = sub.percentageDiscount || this.subsidyRate || 0;
      let subsidyAmount = Math.round(price * (rate / 100));
      if (sub.remainingToday !== undefined && sub.remainingToday !== null && sub.remainingToday > 0) {
        subsidyAmount = Math.min(subsidyAmount, sub.remainingToday);
      } else if (sub.dailyCap > 0) {
        subsidyAmount = Math.min(subsidyAmount, sub.dailyCap);
      }
      return Math.min(price, subsidyAmount);
    }
  }

  updateTransactionsFromBookings(bookings: Booking[]) {
    const list: Transaction[] = [];
    bookings.forEach(b => {
      const originalPrice = b.originalPrice ?? b.price;
      const subsidy = b.subsidyApplied ?? this.calculateSubsidy(originalPrice);
      const coPay = b.amountPaid ?? (originalPrice - subsidy);

      const isCancelled = b.status?.toLowerCase() === 'cancelled' || b.status?.toLowerCase() === 'canceled';

      list.push({
        id: `TXN-${b.id.substring(0, 6)}`,
        date: b.date,
        description: `Co-Pay Contribution (${b.id.substring(0, 8)})`,
        amount: coPay,
        type: 'Debit',
        status: isCancelled ? 'Failed' : 'Success'
      });

      list.push({
        id: `SUB-${b.id.substring(0, 6)}`,
        date: b.date,
        description: `Seplat Corporate Subsidy (${b.id.substring(0, 8)})`,
        amount: subsidy,
        type: 'Subsidy Co-Pay',
        status: isCancelled ? 'Failed' : 'Success'
      });
    });
    this.transactions = list;
  }

  getSelectedDateLabel(): string {
    const selected = this.dates.find(d => d.id === this.selectedDateId);
    return selected ? selected.label : '';
  }

  handleDateSelected(dateId: number) {
    console.log('DEBUG: handleDateSelected called with dateId:', dateId);
    this.selectedDateId = dateId;
    this.loadMenu();
  }

  handleCafeteriaChange(cafeteriaId: string) {
    console.log('DEBUG: handleCafeteriaChange called with cafeteriaId:', cafeteriaId);
    this.selectedCafeteria = cafeteriaId;
    this.loadMenu();
  }

  handleCategorySelected(category: string) {
    console.log('DEBUG: handleCategorySelected called with category:', category);
    this.selectedCategory = category;
    this.loadMenu();
  }

  handleSearchQueryChanged(query: string) {
    console.log('DEBUG: handleSearchQueryChanged called with query:', query);
    this.searchQuery = query;
    this.loadMenu();
  }

  // Cart operations
  saveCart() {
    localStorage.setItem('bucca_cart', JSON.stringify(this.cart));
  }

  syncCartWithLoadedMeals() {
    if (!this.meals || this.meals.length === 0 || !this.cart || this.cart.length === 0) {
      return;
    }
    this.cart.forEach(item => {
      const freshMeal = this.meals.find(m => m.id === item.meal.id);
      if (freshMeal) {
        freshMeal.portionsLeft = Math.max(0, freshMeal.portionsLeft - item.quantity);
        item.meal = freshMeal;
      }
    });
  }

  addToCart(meal: Meal) {
    if (this.isBookingTimePassed()) {
      this.showToast('Booking window has closed for the selected date.', 'error');
      return;
    }

    if (meal.portionsLeft <= 0) {
      this.showToast('This meal is sold out!', 'error');
      return;
    }

    if (this.mealsUsedToday + this.getTotalCartQuantity() >= this.dailyMealLimit) {
      this.showToast('Warning: You are approaching or have reached your daily meal subsidy limit.', 'info');
    }

    const existing = this.cart.find(item => item.meal.id === meal.id);
    if (existing) {
      existing.quantity++;
    } else {
      this.cart.push({ meal, quantity: 1 });
    }

    meal.portionsLeft--;
    this.saveCart();
  }

  removeFromCart(item: CartItem) {
    const idx = this.cart.indexOf(item);
    if (idx !== -1) {
      item.meal.portionsLeft += item.quantity;
      this.cart.splice(idx, 1);
      this.saveCart();
      this.showToast(`Removed ${item.meal.name} from cart`, 'info');
    }
  }

  clearCart() {
    for (const item of this.cart) {
      item.meal.portionsLeft += item.quantity;
    }
    this.cart = [];
    this.saveCart();
    this.showToast('Cart cleared', 'info');
  }

  increaseQuantity(item: CartItem) {
    if (item.meal.portionsLeft <= 0) {
      this.showToast('No more portions available!', 'error');
      return;
    }
    item.quantity++;
    item.meal.portionsLeft--;
    this.saveCart();
  }

  decreaseQuantity(item: CartItem) {
    if (item.quantity > 1) {
      item.quantity--;
      item.meal.portionsLeft++;
    } else {
      this.removeFromCart(item);
    }
    this.saveCart();
  }

  getTotalCartQuantity(): number {
    return this.cart.reduce((sum, item) => sum + item.quantity, 0);
  }

  getTotalCartPrice(): number {
    return this.cart.reduce((sum, item) => sum + (item.meal.price * item.quantity), 0);
  }

  isBookingTimePassed(): boolean {
    const selectedDateOption = this.dates.find(d => d.id === this.selectedDateId);
    if (!selectedDateOption || !selectedDateOption.isoDate) {
      return false;
    }

    const now = new Date();

    // Parse current date to YYYY-MM-DD
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const todayIso = `${year}-${month}-${day}`;

    // If target date is in the past
    if (selectedDateOption.isoDate < todayIso) {
      return true;
    }
    // If target date is in the future
    if (selectedDateOption.isoDate > todayIso) {
      return false;
    }

    // If target date is today, check time
    const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const todayDayName = daysOfWeek[now.getDay()];

    const selectedCafeteriaObj = this.cafeterias.find(c => c.id === this.selectedCafeteria);

    let endTimeStr = null; // Default fallback
    if (selectedCafeteriaObj && selectedCafeteriaObj.bookingWindows && selectedCafeteriaObj.bookingWindows.length > 0) {
      const todayWindow = selectedCafeteriaObj.bookingWindows.find(
        (w: any) => w.dayOfWeek?.toLowerCase() === todayDayName.toLowerCase()
      );
      if (todayWindow) {
        const rawEnd = todayWindow.endHour || todayWindow.endTime;
        if (rawEnd) {
          endTimeStr = rawEnd;
        }
      } else {
        const orgFallback = selectedCafeteriaObj.bookingWindows.find(
          (w: any) => !w.dayOfWeek
        );
        if (orgFallback) {
          const rawEnd = orgFallback.endHour || orgFallback.endTime;
          if (rawEnd) {
            endTimeStr = rawEnd;
          }
        }
      }
    }

    const parts = endTimeStr.split(':');
    const endHours = parseInt(parts[0], 10) || 0;
    const endMinutes = parseInt(parts[1], 10) || 0;
    const endSeconds = parseInt(parts[2], 10) || 0;

    const windowEnd = new Date(now);
    windowEnd.setHours(endHours, endMinutes, endSeconds, 0);

    return now.getTime() > windowEnd.getTime();
  }

  checkout() {
    if (this.isBookingTimePassed()) {
      this.showToast('Booking window has closed for the selected date. Cannot checkout.', 'error');
      return;
    }

    this.lastOriginalPrice = null;
    this.lastSubsidyApplied = null;
    this.lastAmountPaid = null;
    this.lastBookedItems = [];

    this.isCartOpen = false;
    this.bookingState = 'idle';
    this.router.navigate(['/checkout']);
  }

  getCartSubtotal(): number {
    return this.cart.reduce((sum, item) => sum + item.meal.price * item.quantity, 0);
  }

  getCartSubsidyTotal(): number {
    return this.cart.reduce((sum, item) => sum + this.calculateSubsidy(item.meal.price) * item.quantity, 0);
  }

  getCartTotalPayment(): number {
    return Math.max(0, this.getCartSubtotal() - this.getCartSubsidyTotal());
  }

  // Books every item in the cart (one request per unit — the API has no
  // batch/quantity field), aggregates the results, and leaves any items that
  // failed to book in the cart so the user can retry.
  handleConfirmCheckout() {
    if (this.isBookingTimePassed()) {
      this.showToast('Booking window has closed for the selected date. Cannot book meal.', 'error');
      return;
    }

    if (this.cart.length === 0) {
      this.showToast('Your cart is empty.', 'error');
      return;
    }

    const orgId = this.authService.getOrganizationId();
    const userId = this.authService.getUserId();
    if (!orgId || !userId) {
      this.showToast('Session info missing. Please login.', 'error');
      return;
    }

    this.bookingState = 'loading';
    this.cdr.detectChanges();

    const requests = this.cart.flatMap((item) =>
      Array.from({ length: item.quantity }, () =>
        this.staffMealsService.bookMeal({
          mealId: item.meal.id,
          StaffUserId: userId,
          organizationId: orgId
        }).pipe(
          map((res: any) => ({ ok: !!(res && res.status && res.data), res, item })),
          catchError((err) => of({ ok: false, err, item }))
        )
      )
    );

    forkJoin(requests).subscribe((results: any[]) => {
      const succeeded = results.filter((r) => r.ok);
      const failed = results.filter((r) => !r.ok);

      this.lastBookedItems = succeeded.map((r) => {
        const d = r.res.data;
        return {
          mealName: r.item.meal.name,
          vendor: r.item.meal.vendor,
          image: r.item.meal.image,
          cafeteria: r.item.meal.cafeteria,
          category: r.item.meal.category,
          originalPrice: d.originalPrice ?? r.item.meal.price,
          subsidyApplied: d.subsidyApplied ?? 0,
          amountPaid: d.amountPaid ?? r.item.meal.price,
          claimCode: d.claimPasscode || d.claimCode || '',
          bookingReference: d.bookingReference || d.bookingId || d.id || ''
        } as BookedItem;
      });

      this.lastOriginalPrice = this.lastBookedItems.reduce((s, i) => s + i.originalPrice, 0);
      this.lastSubsidyApplied = this.lastBookedItems.reduce((s, i) => s + i.subsidyApplied, 0);
      this.lastAmountPaid = this.lastBookedItems.reduce((s, i) => s + i.amountPaid, 0);
      this.lastBookingReference = this.lastBookedItems[0]?.bookingReference || '';
      this.lastClaimCode = this.lastBookedItems[0]?.claimCode || '';

      // Remove successfully booked quantities from the cart; keep the rest for retry
      const successCountByMealId = new Map<string, number>();
      succeeded.forEach((r) => {
        successCountByMealId.set(r.item.meal.id, (successCountByMealId.get(r.item.meal.id) || 0) + 1);
      });
      this.cart = this.cart
        .map((item) => ({ ...item, quantity: item.quantity - (successCountByMealId.get(item.meal.id) || 0) }))
        .filter((item) => item.quantity > 0);
      this.saveCart();

      if (succeeded.length > 0) {
        this.showToast(
          failed.length > 0
            ? `Booked ${succeeded.length} of ${results.length} item(s) — ${failed.length} failed and remain in your cart.`
            : 'Booking reserved successfully!',
          failed.length > 0 ? 'info' : 'success'
        );
        this.loadBookingHistory();
        this.loadMenu();
        this.refreshProfile();
        this.bookingState = 'success';
      } else {
        this.bookingState = 'error';
        this.showToast('Failed to book meal(s). Please try again.', 'error');
      }
      this.cdr.detectChanges();
    });
  }

  handleCancelBooking(booking: Booking) {
    const orgId = this.authService.getOrganizationId();
    const userId = this.authService.getUserId();
    if (!orgId || !userId) {
      this.showToast('Session info missing. Please login.', 'error');
      return;
    }

    this.staffMealsService.cancelBooking(booking.id, {
      StaffUserId: userId,
      organizationId: orgId
    }).subscribe({
      next: (res) => {
        if (res && res.status) {
          this.showToast('Booking cancelled successfully!', 'success');
          this.loadBookingHistory();
          this.loadMenu();
          this.refreshProfile();
        } else {
          this.showToast(res.message || 'Failed to cancel booking', 'error');
        }
      },
      error: (err) => {
        console.error('Error cancelling booking', err);
        this.showToast(this.extractErrorMessage(err, 'Error cancelling booking from server'), 'error');
      }
    });
  }

  handleNavigation(view: 'home' | 'bookings' | 'transactions' | 'checkout' | 'validate-qr' | 'profile') {
    this.currentView = view;
    this.router.navigate([`/${view}`]);
  }

  showToast(message: string, type: 'success' | 'error' | 'info' = 'success') {
    const id = ++this.toastIdCounter;
    this.toasts.push({ id, message, type });
    this.cdr.detectChanges();

    setTimeout(() => {
      this.toasts = this.toasts.filter(t => t.id !== id);
      this.cdr.detectChanges();
    }, 3000);
  }

  removeToast(id: number) {
    this.toasts = this.toasts.filter(t => t.id !== id);
    this.cdr.detectChanges();
  }

  extractErrorMessage(err: any, fallback: string = 'An error occurred'): string {
    if (!err) return fallback;
    if (typeof err === 'string') return err;

    const errorObj = err.error ?? err;
    if (typeof errorObj === 'string' && errorObj.trim()) return errorObj;

    if (errorObj && typeof errorObj === 'object') {
      if (typeof errorObj.message === 'string' && errorObj.message.trim()) {
        return errorObj.message;
      }
      if (typeof errorObj.Message === 'string' && errorObj.Message.trim()) {
        return errorObj.Message;
      }
      if (typeof errorObj.error === 'string' && errorObj.error.trim()) {
        return errorObj.error;
      }
      if (typeof errorObj.title === 'string' && errorObj.title.trim()) {
        return errorObj.title;
      }
    }

    if (typeof err.message === 'string' && err.message.trim() && !err.message.startsWith('Http failure response')) {
      return err.message;
    }

    return fallback;
  }
}
